import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Ornament from "@/components/Ornament";
import {
  CARNIVAL,
  EXTERIOR_SKETCH,
  INTERIOR_MOOD,
  LOGO_URL,
  PASTA,
  WINE_CELLAR,
  SPRITZ,
  PIZZA_MARGHERITA,
  BURGER,
  CAPRESE,
} from "@/lib/constants";

export default function HomePage() {
  return (
    <Layout>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink" data-testid="hero-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-12 pt-12 md:pt-16 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-10 items-center">
            {/* Left: text */}
            <div className="lg:col-span-4 animate-fadeUp">
              <div className="overline mb-6">Venezia · Locale Storico</div>
              <h1 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl xl:text-6xl leading-[0.95] tracking-tight uppercase">
                CUCINA VENEZIANA
                <br />
                <em className="font-serif italic font-light text-wine" style={{ fontVariantNumeric: "lining-nums" }}>DAL 1971</em>
                <span className="font-serif font-light">, L'EREDITÀ</span>
                <br />
                <span className="font-serif font-light">DI UNA FAMIGLIA.</span>
              </h1>

              <p className="mt-10 max-w-xl text-ink-mid leading-relaxed text-base md:text-lg">
                Due generazioni nel cuore di Venezia. Pesce dell'Adriatico,
                pasta tirata a mano, pizza cotta a legna e una carta che parla
                la lingua del mondo. Quest'anno la nostra insegna riceve
                l'onorificenza di <em>Locale Storico</em>.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/prenotazioni" className="btn-primary" data-testid="hero-book-button">
                  Prenota un tavolo
                </Link>
                <Link to="/storia" className="btn-outline" data-testid="hero-story-button">
                  La nostra storia
                </Link>
              </div>
            </div>

            {/* Right: image with seal */}
            <div className="lg:col-span-7 relative lg:translate-x-8 xl:translate-x-16">
              <img
                src={CARNIVAL}
                alt="Ristorante Pedrocchi — illustrazione"
                className="w-full h-auto block border border-ink"
                loading="eager"
              />
              <div className="absolute -bottom-8 -left-8 md:-bottom-10 md:-left-10 seal" data-testid="locale-storico-seal">
                <span className="seal-cap">Locale</span>
                <span className="seal-year">2026</span>
                <span className="seal-cap">Storico</span>
              </div>
              <div className="absolute -top-6 -right-6 hidden md:block">
                <img src={LOGO_URL} alt="" className="h-24 w-auto opacity-90" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="border-b border-ink bg-paper-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          <div>
            <div className="overline mb-2">Dal</div>
            <div className="font-serif text-5xl md:text-6xl text-ink">1971</div>
            <p className="text-ink-mid text-sm mt-2">Paolo e Renata Puntar aprono le porte del locale.</p>
          </div>
          <div className="md:border-x md:border-ink md:px-10">
            <div className="overline mb-2">Famiglia</div>
            <div className="font-serif text-5xl md:text-6xl text-ink">Puntar</div>
            <p className="text-ink-mid text-sm mt-2">Dal 2023 i figli Manuel e Cristiana proseguono la tradizione.</p>
          </div>
          <div>
            <div className="overline mb-2">Cucina</div>
            <div className="font-serif text-5xl md:text-6xl text-ink">Veneziana</div>
            <p className="text-ink-mid text-sm mt-2">Sapori veneziani aperti al mondo, per ogni cultura e gusto.</p>
          </div>
        </div>
      </section>

      {/* PIATTI / SPECIALITÀ */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="overline mb-4">Specialità della casa</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Il sapore della laguna,<br />
                <em className="text-wine">il calore di casa.</em>
              </h2>
              <p className="text-ink-mid mt-6 leading-relaxed">
                Bigoli in salsa, baccalà mantecato, fegato alla veneziana, fritto
                misto dell'Adriatico. E poi la nostra pizza cotta a legna, viva
                dal 1971. Una cucina autentica che non rinuncia all'apertura
                verso gusti internazionali.
              </p>
              <Link to="/menu" className="mt-8 inline-block btn-outline" data-testid="home-menu-link">
                Sfoglia il menu
              </Link>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
              <img src={SPRITZ} alt="Spritz veneziano" className="w-full aspect-square object-cover duotone" />
              <img src={PIZZA_MARGHERITA} alt="Pizza margherita cotta a legna" className="w-full aspect-square object-cover duotone mt-10" />
              <img src={BURGER} alt="Hamburger gourmet della casa" className="w-full aspect-square object-cover duotone" />
              <img src={CAPRESE} alt="Insalata caprese" className="w-full aspect-square object-cover duotone mt-10" />
            </div>
          </div>
        </div>
      </section>

      <Ornament />

      {/* QUOTE */}
      <section className="section pt-0">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="overline mb-6">Carta a Manuel &amp; Cristiana</div>
          <blockquote className="font-serif text-3xl md:text-5xl leading-snug italic text-ink">
            "Cinquant'anni di servizio non si conservano, si rinnovano ogni
            giorno. Una forchetta alla volta."
          </blockquote>
          <div className="overline mt-8">— La famiglia Puntar</div>
        </div>
      </section>

      {/* RESERVATION CTA */}
      <section className="border-y border-ink bg-paper-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline mb-4">Prenotazioni</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Riserva il tuo tavolo
            </h2>
            <p className="text-ink-mid mt-4 max-w-md leading-relaxed">
              Per cene di famiglia, ricorrenze, incontri d'affari o semplicemente
              per una pizza fra amici. Vi aspettiamo.
            </p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/prenotazioni" className="btn-primary" data-testid="cta-book-button">
              Prenota ora
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
